import config from "../config";

// The loading-screen quotes live in the cloud, not in this bundle. They are
// needed the instant a loading state renders, so this store keeps three things
// in step: an in-memory copy (the snapshot React subscribes to), a localStorage
// mirror read synchronously at module load so a returning visitor has a quote
// at first paint, and at most ONE network fetch per session.
//
// Every localStorage access is wrapped: the accessor itself throws in some
// contexts (private mode, site data blocked), and an unguarded throw here would
// happen during module evaluation and white-screen the entire site for that
// visitor, permanently.

export const CACHE_KEY = "artefactly.quotes.v1";
const MAX_TEXT = 140; // mirrors the Lambda's cap; a longer string is not ours

let quotes = [];
let inflight = null;
let loaded = false; // true only after a fetch has SUCCEEDED this session
const listeners = new Set();

// The cache is a second, unvalidated input — anyone can edit localStorage. It
// gets the same shape check as a network response.
function clean(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((q) => typeof q === "string" && q.length > 0 && q.length <= MAX_TEXT);
}

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return [];
    return clean(parsed.quotes);
  } catch (e) {
    return [];
  }
}

function writeCache(list) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), quotes: list }));
  } catch (e) {
    // A full or unavailable store is not worth breaking a page load over.
  }
}

function publish(next) {
  const changed =
    next.length !== quotes.length || next.some((q, i) => q !== quotes[i]);
  // useSyncExternalStore compares snapshots by reference, so the array is only
  // replaced when the contents actually differ — otherwise React would see a
  // new snapshot on every read and re-render forever.
  if (!changed) return;
  quotes = next;
  listeners.forEach((fn) => fn());
}

quotes = readCache();

export function getSnapshot() {
  return quotes;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Load the corpus once per session. Safe to call from every component that
 * needs a quote, no matter how many are mounted or when: concurrent calls
 * share one request, and once a request has SUCCEEDED this session every
 * later call — concurrent or sequential — is a no-op that returns the
 * existing corpus rather than refetching it. A failure leaves whatever was
 * already cached in place and stays retryable, since nothing has actually
 * loaded yet. A full page reload starts a new session (this module
 * re-evaluates) and fetches again, which is how admin edits reach visitors.
 */
export function ensureLoaded() {
  if (loaded) return Promise.resolve(quotes);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(config.quotesApiUrl);
      if (!res.ok) return quotes;
      const data = await res.json();
      const next = clean(data && data.quotes);
      // A successful response is authoritative even when it's empty — the
      // admin may have hidden every quote to retract them from the site, and
      // that corpus must overwrite the cache. Only a FAILED fetch (the
      // early return above, or the catch below) should leave the cache be.
      publish(next);
      writeCache(next);
      loaded = true;
      return quotes;
    } catch (e) {
      return quotes;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Test seam: drop every in-memory trace and re-read the cache. */
export function __resetForTests() {
  quotes = [];
  inflight = null;
  loaded = false;
  listeners.clear();
  quotes = readCache();
}
